/* C73: current Shopify best-selling order, never copurchase or stale snapshots.
 * Only GET section rendering; native card identities/prices are never invented. */
(() => {
  'use strict';
  if (window.MiniamiCartRecommendations) return;
  const selector = '[data-cart-recommendations]';
  const pending = new Map();
  const generations = new WeakMap();
  const validID = (id) => typeof id === 'string' && /^[1-9]\d*$/.test(id);
  const validCard = (item) => validID(item.dataset.cartRecommendationId)
    && item.dataset.cartRecommendationAvailable === 'true'
    && item.dataset.cartRecommendationRole === 'standalone-digital-pattern'
    && item.dataset.cartRecommendationRoleSource === 'native-custom.product_role'
    && !!item.dataset.cartRecommendationHandle;
  const feedURL = (value) => {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?collections\/[^/]+$/.test(url.pathname)
      || url.searchParams.get('sort_by') !== 'best-selling'
      || url.searchParams.get('section_id') !== 'miniami-cart-bestsellers'
      || [...url.searchParams.keys()].some((key) => !['sort_by', 'section_id'].includes(key))) throw new Error('invalid feed');
    return url;
  };
  async function loadFeed(value, themeID) {
    const url = feedURL(value);
    const key = `${themeID}:${url.href}`;
    if (pending.has(key)) return pending.get(key);
    const promise = (async () => {
      const items = [], seen = new Set();
      let pages = 1, total = null, renderedProducts = 0;
      for (let page = 1; page <= pages; page += 1) {
        const next = new URL(url); next.searchParams.set('page', String(page));
        const response = await fetch(next.href, { method: 'GET', credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(8000) });
        if (!response.ok || new URL(response.url || next.href).origin !== url.origin) throw new Error('feed unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const feeds = doc.querySelectorAll('[data-cart-bestseller-feed]');
        const feed = feeds.length === 1 ? feeds[0] : null;
        const count = Number(feed?.dataset.feedPages), size = Number(feed?.dataset.feedTotal);
        const pageProducts = Number(feed?.dataset.feedProductCount);
        if (!feed || feed.dataset.cartBestsellerSort !== 'best-selling' || feed.dataset.themeId !== themeID
          || Number(feed.dataset.feedPage) !== page || !Number.isSafeInteger(count) || count < 1 || count > 20
          || !Number.isSafeInteger(size) || size < 0 || !Number.isSafeInteger(pageProducts) || pageProducts < 0 || pageProducts > 250
          || feed.querySelectorAll('[data-cart-recommendation-id]').length > pageProducts
          || (total !== null && (total !== size || count !== pages))) throw new Error('incomplete feed');
        pages = count; total = size;
        renderedProducts += pageProducts;
        for (const item of feed.querySelectorAll('[data-cart-recommendation-id]')) {
          if (!validCard(item) || seen.has(item.dataset.cartRecommendationId)) throw new Error('invalid ranked card');
          seen.add(item.dataset.cartRecommendationId); items.push(item);
        }
      }
      if (renderedProducts !== total) throw new Error('truncated feed');
      return items;
    })();
    pending.set(key, promise);
    try { return await promise; } finally { pending.delete(key); }
  }
  async function render(root) {
    const rail = root.querySelector('.mn-cart-upsell__rail');
    if (!rail) return;
    const generation = (generations.get(root) || 0) + 1; generations.set(root, generation);
    // Never show collection-default cards while claiming bestseller ordering.
    root.hidden = true; root.dataset.recommendationSource = 'awaiting-native-best-selling';
    if (root.dataset.cartRecommendationsResolved !== 'true') return;
    try {
      const raw = JSON.parse(root.querySelector('[data-cart-recommendation-exclusions]')?.textContent || 'null');
      if (!Array.isArray(raw) || raw.some((id) => id !== '' && !validID(id))) throw new Error('invalid exclusions');
      const excluded = new Set(raw.filter(validID));
      const themeID = String(window.Shopify?.theme?.id || root.dataset.themeId || '');
      if (!validID(themeID) || (root.dataset.themeId && root.dataset.themeId !== themeID)) throw new Error('theme mismatch');
      const ranked = await loadFeed(root.dataset.cartBestsellerUrl, themeID);
      if (generations.get(root) !== generation || !root.isConnected) return;
      const chosen = ranked.filter((item) => !excluded.has(item.dataset.cartRecommendationId)).slice(0, 3);
      const label = rail.querySelector('[data-miniami-upsell-submit]')?.textContent || '+ Add';
      rail.replaceChildren(...chosen.map((item) => {
        const card = document.importNode(item, true);
        card.hidden = false;
        card.querySelector('[data-miniami-upsell-submit]').textContent = label;
        return card;
      }));
      root.hidden = !chosen.length;
      root.dataset.recommendationSource = 'shopify-best-selling';
      root.dataset.recommendationMetric = 'Shopify native best-selling collection sort';
    } catch (_) {
      // Honest unavailable state, not a silent stale/default/copupurchase substitute.
      if (generations.get(root) === generation) {
        root.hidden = true; root.dataset.recommendationSource = 'native-best-selling-unavailable';
      }
    }
  }
  function refresh() { return Promise.all([...document.querySelectorAll(selector)].map(render)); }
  let scheduled = false;
  const schedule = () => {
    if (scheduled) return;
    scheduled = true; queueMicrotask(() => { scheduled = false; void refresh(); });
  };
  function start() {
    void refresh();
    const observer = new MutationObserver((records) => {
      if (records.some((record) => [...record.addedNodes].some((node) => node.nodeType === 1
        && (node.matches?.(selector) || node.querySelector?.(selector))))) schedule();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
  window.MiniamiCartRecommendations = Object.freeze({ refresh });
  document.addEventListener('miniami:parallel-cart-updated', schedule);
  document.addEventListener('shopify:section:load', schedule);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
